/**
 * Image Tone Analyzer
 * Analyzes the overall brightness/tone of images to determine optimal text contrast
 */

class ImageToneAnalyzer {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Analyze image tone and return brightness information
   * @param {string} imageUrl - URL of the image to analyze
   * @param {Object} options - Configuration options
   * @returns {Promise<Object>} Analysis result with brightness and contrast info
   */
  async analyzeImage(imageUrl, options = {}) {
    const {
      sampleSize = 50,
      useCache = true,
      maxImageSize = 100
    } = options;

    // Check cache first
    if (useCache && this.cache.has(imageUrl)) {
      return this.cache.get(imageUrl);
    }

    try {
      const result = await this._processImage(imageUrl, sampleSize, maxImageSize);
      
      if (useCache) {
        this.cache.set(imageUrl, result);
      }
      
      return result;
    } catch (error) {
      console.warn('Image tone analysis failed:', error);
      return this._getDefaultResult();
    }
  }

  /**
   * Analyze background image of an element
   * @param {HTMLElement} element - Element with background image
   * @param {Object} options - Configuration options
   * @returns {Promise<Object>} Analysis result
   */
  async analyzeElementBackground(element, options = {}) {
    // Check the element and its children for background images
    const elementsToCheck = [element, ...element.querySelectorAll('*')];
    
    for (const el of elementsToCheck) {
      const computedStyle = window.getComputedStyle(el);
      const backgroundImage = computedStyle.backgroundImage;
      
      if (backgroundImage && backgroundImage !== 'none') {
        console.log(`🖼️ Found background image on:`, el.tagName, el.className || '(no class)');
        // Extract URL from CSS background-image property
        const imageUrl = backgroundImage.slice(4, -1).replace(/["']/g, '');
        console.log(`🔗 Image URL:`, imageUrl);
        return this.analyzeImage(imageUrl, options);
      }
    }
    
    // Also check for img elements within the container
    const imgElements = element.querySelectorAll('img');
    if (imgElements.length > 0) {
      const img = imgElements[0]; // Use the first image found
      console.log(`🖼️ Found img element, using src:`, img.src);
      return this.analyzeImage(img.src, options);
    }
    
    throw new Error('No background image or img element found on element or its children');
  }

  /**
   * Apply optimal text styling based on image analysis
   * @param {HTMLElement} textElement - Element to style
   * @param {Object} analysisResult - Result from analyzeImage
   * @param {Object} options - Styling options
   */
  applyOptimalTextStyling(textElement, analysisResult, options = {}) {
    const {
      lightColor = '#ffffff',
      darkColor = '#000000'
    } = options;

    const { isDark } = analysisResult;

    // Set text color based on background brightness
    textElement.style.color = isDark ? lightColor : darkColor;

    // Add data attribute for CSS targeting
    textElement.setAttribute('data-text-tone', isDark ? 'light' : 'dark');
  }

  /**
   * Add overlay to image when text is darkened for better contrast
   * @param {HTMLElement} imageWrapper - The image wrapper element
   * @param {Object} analysisResult - Result from analyzeImage
   */
  applyImageOverlay(imageWrapper, analysisResult) {
    const { isDark } = analysisResult;
    
    // Remove any existing auto-contrast overlay
    const existingOverlay = imageWrapper.querySelector('.auto-contrast-overlay');
    if (existingOverlay) {
      existingOverlay.remove();
    }
    
    // Only add overlay when background is light (text will be dark)
    if (!isDark) {
      console.log('🎭 Adding 10% black overlay to lighten background for dark text');
      
      const overlay = document.createElement('div');
      overlay.className = 'auto-contrast-overlay';
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.1);
        pointer-events: none;
        z-index: 1;
      `;
      
      // Make sure the image wrapper is positioned
      const wrapperStyle = window.getComputedStyle(imageWrapper);
      if (wrapperStyle.position === 'static') {
        imageWrapper.style.position = 'relative';
      }
      
      imageWrapper.appendChild(overlay);
    } else {
      console.log('🎭 No overlay needed - background is dark, text is white');
    }
  }

  /**
   * Auto-analyze and style text elements within a container
   * @param {HTMLElement} container - Container with background image
   * @param {string} textSelector - CSS selector for text elements to style
   * @param {Object} options - Configuration options
   */
  async autoStyleTextElements(container, textSelector = 'h1, h2, h3, .collection-hero__title, .collection-hero__description', options = {}) {
    try {
      console.log(`🎯 Starting auto text styling for container:`, container);
      console.log(`🔍 Looking for text elements: ${textSelector}`);
      
      const analysisResult = await this.analyzeElementBackground(container, options);
      const textElements = container.querySelectorAll(textSelector);
      
      console.log(`📝 Found ${textElements.length} text elements to style`);
      console.log(`🎨 Applying ${analysisResult.isDark ? 'WHITE' : 'BLACK'} text color`);
      
      textElements.forEach((element, index) => {
        console.log(`  ${index + 1}. Styling:`, element.tagName, element.className || '(no class)');
        this.applyOptimalTextStyling(element, analysisResult, options);
      });

      // Add class to container for additional CSS targeting
      const containerClass = analysisResult.isDark ? 'auto-text-light' : 'auto-text-dark';
      container.classList.add(containerClass);
      console.log(`✅ Added class '${containerClass}' to container`);
      
      return analysisResult;
    } catch (error) {
      console.warn('❌ Auto text styling failed:', error);
      return null;
    }
  }

  /**
   * Process image and calculate brightness
   * @private
   */
  async _processImage(imageUrl, sampleSize, maxImageSize) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Scale down image for performance
          const scale = Math.min(maxImageSize / img.width, maxImageSize / img.height);
          canvas.width = Math.max(1, Math.floor(img.width * scale));
          canvas.height = Math.max(1, Math.floor(img.height * scale));
          
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          console.log(`🖼️ Analyzing image: ${canvas.width}x${canvas.height} pixels`);
          
          let totalBrightness = 0;
          let sampleCount = 0;
          let minBrightness = 255;
          let maxBrightness = 0;
          const brightnessSamples = [];
          
          // Sample pixels for brightness calculation
          const step = Math.max(4, Math.floor(data.length / (sampleSize * sampleSize)));
          console.log(`📊 Sampling every ${step/4}th pixel (${Math.floor(data.length/step)} total samples)`);
          
          for (let i = 0; i < data.length; i += step) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            
            // Calculate perceived brightness using luminance formula
            const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
            totalBrightness += brightness;
            sampleCount++;
            
            // Track min/max for analysis
            minBrightness = Math.min(minBrightness, brightness);
            maxBrightness = Math.max(maxBrightness, brightness);
            
            // Store first 10 samples for detailed logging
            if (brightnessSamples.length < 10) {
              brightnessSamples.push({
                rgb: `rgb(${r},${g},${b})`,
                brightness: Math.round(brightness)
              });
            }
          }
          
          const averageBrightness = totalBrightness / sampleCount;
          
          // Log detailed analysis
          console.log(`🎨 Sample pixels:`, brightnessSamples);
          console.log(`📈 Brightness range: ${Math.round(minBrightness)} - ${Math.round(maxBrightness)}`);
          console.log(`📊 Average brightness: ${Math.round(averageBrightness)} / 255`);
          console.log(`🎯 Decision threshold: 128 (middle point)`);
          console.log(`💡 Result: ${averageBrightness < 128 ? 'DARK' : 'LIGHT'} background → ${averageBrightness < 128 ? 'WHITE' : 'BLACK'} text`);
          
          resolve({
            brightness: averageBrightness,
            isDark: averageBrightness < 128,
            isLight: averageBrightness >= 128,
            textColor: averageBrightness < 128 ? '#ffffff' : '#000000',
            recommendedShadow: averageBrightness < 128 
              ? '2px 2px 4px rgba(0,0,0,0.8)' 
              : '1px 1px 2px rgba(255,255,255,0.8)'
          });
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  }

  /**
   * Get default result for fallback
   * @private
   */
  _getDefaultResult() {
    return {
      brightness: 128,
      isDark: false,
      isLight: true,
      textColor: '#000000',
      recommendedShadow: '1px 1px 2px rgba(255,255,255,0.8)'
    };
  }

  /**
   * Clear the analysis cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Initialize global instance
window.ImageToneAnalyzer = ImageToneAnalyzer;

// Auto-initialize for collection banners when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const analyzer = new ImageToneAnalyzer();
  
  // Find collection hero sections with auto-contrast enabled
  const collectionHeroes = document.querySelectorAll('.collection-hero__wrapper[data-auto-text-contrast="true"]');
  
  console.log(`🔍 Found ${collectionHeroes.length} collection banner(s) with auto-contrast enabled`);
  
  collectionHeroes.forEach((hero, index) => {
    // Wait a bit for images to load, stagger the requests
    setTimeout(() => {
      console.log(`🚀 Processing collection banner ${index + 1}/${collectionHeroes.length}`);
      
      // Look specifically for the image wrapper element
      const imageWrapper = hero.querySelector('.collection-hero__image-wrapper');
      
      if (!imageWrapper) {
        console.log(`⚠️ Banner ${index + 1} has no .collection-hero__image-wrapper, applying default black text`);
        
        // Apply default black text styling when no image wrapper exists
        const textElements = hero.querySelectorAll('.collection-hero__title, .collection-hero__description, .breadcrumb a, .breadcrumb span');
        console.log(`📝 Applying default BLACK text to ${textElements.length} elements (no image wrapper)`);
        
        textElements.forEach((element, textIndex) => {
          console.log(`  ${textIndex + 1}. Default styling:`, element.tagName, element.className || '(no class)');
          element.style.color = '#000000';
          element.setAttribute('data-text-tone', 'dark');
        });
        
        // Add class for CSS targeting
        hero.classList.add('auto-text-dark');
        console.log(`✅ Added class 'auto-text-dark' to banner ${index + 1} (no image wrapper default)`);
        
        return;
      }
      
      // Check if the image wrapper has images
      const hasImgElements = imageWrapper.querySelectorAll('img').length > 0;
      const hasBackgroundImage = window.getComputedStyle(imageWrapper).backgroundImage !== 'none';
      
      if (!hasImgElements && !hasBackgroundImage) {
        console.log(`⚠️ Banner ${index + 1} has no images, applying default black text`);
        
        // Apply default black text styling when no images are present
        const textElements = hero.querySelectorAll('.collection-hero__title, .collection-hero__description, .breadcrumb a, .breadcrumb span');
        console.log(`📝 Applying default BLACK text to ${textElements.length} elements (no image)`);
        
        textElements.forEach((element, textIndex) => {
          console.log(`  ${textIndex + 1}. Default styling:`, element.tagName, element.className || '(no class)');
          element.style.color = '#000000';
          element.setAttribute('data-text-tone', 'dark');
        });
        
        // Add class for CSS targeting
        hero.classList.add('auto-text-dark');
        console.log(`✅ Added class 'auto-text-dark' to banner ${index + 1} (no image default)`);
        
        return;
      }
      
      console.log(`🎯 Analyzing images in .collection-hero__image-wrapper for banner ${index + 1}`);
      
      // Analyze the image wrapper specifically, but apply styling to the main hero container
      analyzer.analyzeElementBackground(imageWrapper).then(result => {
        console.log(`📊 Image analysis complete for banner ${index + 1}:`, {
          brightness: Math.round(result.brightness),
          isDark: result.isDark,
          textColor: result.textColor
        });
        
        // Apply styling to text elements in the main hero container
        const textElements = hero.querySelectorAll('.collection-hero__title, .collection-hero__description, .breadcrumb a, .breadcrumb span');
        console.log(`📝 Applying ${result.isDark ? 'WHITE' : 'BLACK'} text to ${textElements.length} elements`);
        
        textElements.forEach((element, textIndex) => {
          console.log(`  ${textIndex + 1}. Styling:`, element.tagName, element.className || '(no class)');
          analyzer.applyOptimalTextStyling(element, result);
        });
        
        // Apply overlay to image when text is dark (for better contrast)
        analyzer.applyImageOverlay(imageWrapper, result);
        
        // Add class to main container for CSS targeting
        const containerClass = result.isDark ? 'auto-text-light' : 'auto-text-dark';
        hero.classList.add(containerClass);
        console.log(`✅ Added class '${containerClass}' to banner ${index + 1}`);
        
      }).catch(error => {
        console.log(`⚠️ Banner ${index + 1} auto-styling failed:`, error.message);
      });
    }, (index * 200) + 500); // Stagger requests and wait for initial load
  });
});
